'use client'

import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTheme } from '@/state/theme'
import { useShouldAnimate } from '@/hooks/use-should-animate'
import { cn } from '@/lib/utils'

/**
 * Loading overlay that appears when the application theme is changing.
 * Uses CSS animations instead of framer-motion to keep the heavy motion
 * runtime out of the initial paint bundle.
 */
export function ThemeLoader() {
  const { isThemeChanging } = useTheme()
  const [showStatus, setShowStatus] = useState(false)
  const [mounted, setMounted] = useState(false)
  const shouldAnimate = useShouldAnimate()

  useEffect(() => {
    if (isThemeChanging) {
      setShowStatus(true)
      setMounted(true)
      return
    }
    if (!mounted) return
    const exitDuration = shouldAnimate ? 300 : 0
    const timer = window.setTimeout(() => setMounted(false), exitDuration)
    return () => window.clearTimeout(timer)
  }, [isThemeChanging, mounted, shouldAnimate])

  return (
    <>
      {/* Persistent live region for completion announcement */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {isThemeChanging ? 'Ativando tema, por favor aguarde' : showStatus ? 'Tema aplicado com sucesso' : ''}
      </div>

      {mounted && (
        <div
          data-theme-loader-state={isThemeChanging ? 'open' : 'closed'}
          className={cn(
            'fixed inset-0 z-[9999] flex items-center justify-center bg-background',
            shouldAnimate && 'transition-opacity duration-300 ease-in-out',
            isThemeChanging ? 'opacity-100' : 'opacity-0',
          )}
          aria-hidden="true"
        >
          <div
            className={cn(
              'flex flex-col items-center gap-6 rounded-2xl border border-primary/20 bg-card p-8 shadow-2xl shadow-primary/10',
              shouldAnimate && 'transition-transform duration-300 ease-out',
              shouldAnimate && (isThemeChanging ? 'scale-100' : 'scale-95'),
            )}
            aria-labelledby="theme-loader-title"
            aria-describedby="theme-loader-desc"
          >
            <div className="relative">
              <Loader2
                className={cn('h-12 w-12 text-primary', shouldAnimate && 'animate-spin')}
                strokeWidth={2.5}
                aria-hidden="true"
              />
              <div
                className={cn(
                  'absolute inset-0 -z-10 rounded-full bg-primary/20 blur-xl',
                  shouldAnimate ? 'animate-pulse opacity-50' : 'opacity-30',
                )}
                aria-hidden="true"
              />
            </div>

            <div className="flex flex-col items-center gap-2 text-center">
              <span id="theme-loader-title" className="text-base font-bold uppercase tracking-widest text-primary">
                Ativando Tema
              </span>
              <span
                id="theme-loader-desc"
                className={cn('text-sm font-medium text-muted-foreground', shouldAnimate && 'animate-pulse')}
              >
                Ajustando interface...
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
